import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { GREAT_WHITE, EERIE, OLAF } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITable from 'UIComponents/table/UITable';
import { ApiNameToProductTranslationKeyMap } from 'pricing-pages-ui/constants/ApiNameToProductTranslationKeyMap';
import { FREE } from 'self-service-api/constants/ProductLevels';
import { getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { HUBSPOT_FREE } from 'self-service-api/constants/ApiNames';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { getFeaturesByProductLineSelector } from '../../../selectors/FeatureSelectors';
import { hasPricingAndPurchasingSucceededSelector, hasProductsSucceededSelector } from '../../../selectors/LoadingStateSelectors';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import FeatureTableCol from '../../feature-table/FeatureTableCol';
import FeatureTableColTag from '../../feature-table/FeatureTableColTag';
import FeatureTablePriceDisplay from '../../feature-table/FeatureTablePriceDisplay';
import FeatureTableRow from '../../feature-table/FeatureTableRow';
import { Section } from '../../common/SharedComponents';
const GradientOverlay = styled.div.withConfig({
  displayName: "MobileFeatureTable__GradientOverlay",
  componentId: "vk1a-0"
})(["position:relative;overflow:hidden;max-height:", ";&::after{visibility:", ";opacity:", ";position:absolute;bottom:0;height:100%;width:100%;content:'';background:linear-gradient(to bottom,transparent 70%,", ");pointer-events:none;}"], props => props.expanded ? '100%' : '650px', props => props.expanded ? 'hidden' : 'visible', props => props.expanded ? 0 : 1, OLAF);
const THead = styled.thead.withConfig({
  displayName: "MobileFeatureTable__THead",
  componentId: "vk1a-1"
})(["tr{position:relative;}& tr::after{content:'';position:absolute;bottom:0;left:0;right:0;border:1px solid transparent;box-shadow:0 2px 4px ", ";}& tr th{background-color:transparent;border:none;font-size:0.875rem;text-transform:inherit;text-align:center;font-weight:600;white-space:nowrap;padding-top:12px;padding-bottom:8px;position:relative;}& tr th:first-of-type{font-size:1.5rem;text-align:left;line-height:100%;vertical-align:text-top;}"], GREAT_WHITE);
const TBody = styled.tbody.withConfig({
  displayName: "MobileFeatureTable__TBody",
  componentId: "vk1a-2"
})(["& tr > *{border:1px solid ", ";}& tr td{text-align:center;}& tr th[scope='row']{text-align:left;padding:8px;}"], GREAT_WHITE);

const MobileFeatureTable = ({
  productLine,
  highlightedProducts,
  screen,
  tab
}) => {
  const [expanded, setExpanded] = useState(false);
  const features = useSelector(state => {
    return getFeaturesByProductLineSelector(state, {
      productLine
    });
  });
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const hasProductsSucceeded = useSelector(hasProductsSucceededSelector);

  const handleExpandClick = () => {
    getUsageTracker().track('featureTableInteraction', {
      action: `clicked-${expanded ? 'collapse' : 'expand'}-table`,
      productLine,
      products: highlightedProducts,
      screen,
      tab
    });
    setExpanded(prev => !prev);
  };

  if (!features.length) return null;
  return /*#__PURE__*/_jsxs(Section, {
    children: [/*#__PURE__*/_jsx(GradientOverlay, {
      expanded: expanded,
      children: /*#__PURE__*/_jsxs(UITable, {
        bordered: false,
        hover: false,
        condensed: true,
        className: "m-top-8",
        children: [/*#__PURE__*/_jsxs("colgroup", {
          children: [/*#__PURE__*/_jsx("col", {}), highlightedProducts.map(apiName => {
            return /*#__PURE__*/_jsx(FeatureTableCol, {
              apiName: apiName,
              productLine: productLine
            }, apiName);
          })]
        }), /*#__PURE__*/_jsx(THead, {
          children: /*#__PURE__*/_jsxs("tr", {
            children: [/*#__PURE__*/_jsx("th", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "upgrades.productCatalog.tableHeader.features"
              })
            }), highlightedProducts.map(apiName => {
              const apiNameForPrice = getProductLevelFromApiNameString(apiName) === FREE ? HUBSPOT_FREE : apiName;
              return /*#__PURE__*/_jsxs("th", {
                scope: "col",
                children: [/*#__PURE__*/_jsx(FeatureTableColTag, {
                  apiName: apiName,
                  productLine: productLine
                }), /*#__PURE__*/_jsx("div", {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: ApiNameToProductTranslationKeyMap[apiName]
                  })
                }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
                  isLoading: !hasPricingAndPurchasingSucceeded || !hasProductsSucceeded,
                  heights: [20],
                  widths: [60],
                  className: "flex-column align-center",
                  Content: FeatureTablePriceDisplay,
                  contentProps: {
                    apiName: apiNameForPrice
                  }
                })]
              }, apiName);
            })]
          })
        }), /*#__PURE__*/_jsx(TBody, {
          children: features.map(feature => {
            return /*#__PURE__*/_jsx(FeatureTableRow, {
              highlightedProducts: highlightedProducts,
              feature: feature,
              productLine: productLine
            }, feature.featureId);
          })
        })]
      })
    }), /*#__PURE__*/_jsx("div", {
      className: "text-center justify-center align-center",
      children: /*#__PURE__*/_jsxs(UIButton, {
        use: "transparent",
        onClick: handleExpandClick,
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: expanded ? 'featureTable.collapse' : 'featureTable.expand'
        }), /*#__PURE__*/_jsx(UIIcon, {
          name: expanded ? 'up' : 'down',
          size: "xxs",
          color: EERIE
        })]
      })
    })]
  });
};

export default MobileFeatureTable;